import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import {
    Search,
    Target,
    Award,
    CheckCircle,
    Clock,
    TrendingUp,
    Play,
    Eye,
    Loader2,
    RefreshCw,
    Filter,
    ArrowUpDown,
    CheckCircle2,
    XCircle,
    Users,
    Star,
    Zap,
    Trophy,
    Briefcase
} from 'lucide-react';

const SourcingLabsAnalytics = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [labs, setLabs] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterDifficulty, setFilterDifficulty] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    // Fetch paginated sessions with infinite scroll
    const fetchSessions = useCallback(async (page, perPage) => {
        const params = new URLSearchParams({
            page: page.toString(),
            per_page: perPage.toString(),
            sort_by: sortField,
            sort_order: sortOrder,
        });
        if (filterDifficulty !== 'all') params.append('difficulty', filterDifficulty);
        if (filterStatus !== 'all') params.append('status', filterStatus);
        
        const response = await api.get(`/sourcing-labs/sessions/history?${params}`);
        return response.data;
    }, [api, sortField, sortOrder, filterDifficulty, filterStatus]);

    const {
        items: sessions,
        loading: sessionsLoading,
        loadingMore,
        hasMore,
        total,
        refresh: refreshSessions,
        loadMoreRef,
    } = useInfiniteScroll(fetchSessions, {
        perPage: 20,
        enabled: !loading, // Enable after initial data loads
    });

    useEffect(() => {
        fetchData();
    }, []);

    // Refresh sessions when filters change
    useEffect(() => {
        if (!loading) {
            refreshSessions();
        }
    }, [filterDifficulty, filterStatus, sortField, sortOrder]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch stats, labs, and gamification progress
            const [statsRes, labsRes, gamificationRes] = await Promise.all([
                api.get('/sourcing-labs/stats/me'),
                api.get('/sourcing-labs/'),
                api.get('/sourcing-labs/gamification/my-progress').catch(() => ({ data: null }))
            ]);
            
            setStats(statsRes.data || {});
            setLabs(labsRes.data || []);
            
            // Add gamification data if available
            if (gamificationRes.data) {
                setStats(prev => ({
                    ...prev,
                    gamification: gamificationRes.data
                }));
            }
            
        } catch (error) {
            console.error('Failed to fetch sourcing data:', error);
            toast.error('Failed to load sourcing labs data');
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
        if (score >= 60) return 'text-amber-600 dark:text-amber-400';
        return 'text-red-600 dark:text-red-400';
    };

    const getDifficultyBadge = (difficulty) => {
        const colors = {
            beginner: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
            intermediate: 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30',
            advanced: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
            expert: 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30'
        };
        return (
            <Badge variant="outline" className={`capitalize ${colors[difficulty] || colors.beginner}`}>
                {difficulty || 'Beginner'}
            </Badge>
        );
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'completed':
                return <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">Completed</Badge>;
            case 'active':
                return <Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-300">Active</Badge>;
            case 'in_progress':
                return <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-300">In Progress</Badge>;
            default:
                return <Badge variant="outline">{status || 'Unknown'}</Badge>;
        }
    };

    const viewSessionDetail = async (session) => {
        try {
            const response = await api.get(`/sourcing-labs/sessions/${session.id}`);
            setSelectedSession(response.data);
            setShowDetailModal(true);
        } catch (error) {
            setSelectedSession(session);
            setShowDetailModal(true);
        }
    };

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    // Calculate stats from stats API (not from sessions anymore)
    const completedCount = stats?.total_sessions || 0;
    const avgScore = stats?.average_score || 0;
    const highestScore = stats?.highest_score || 0;
    const passingCount = sessions.filter(s => (s.scores?.overall || 0) >= 80).length;

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="sourcing-analytics">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Search className="h-7 w-7 text-primary" />
                            Sourcing Labs Analytics
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Track your candidate sourcing skills and performance
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => { fetchData(); refreshSessions(); }}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                        <Button onClick={() => navigate('/sourcing-labs')}>
                            <Play className="h-4 w-4 mr-2" />
                            New Lab
                        </Button>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.total_sessions || 0}</p>
                            <p className="text-xs text-muted-foreground">Total Labs</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{completedCount}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                        </CardContent>
                    </Card>
                    <Card className="border-emerald-500/30 bg-emerald-500/5">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{passingCount}</p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Passed (80+)</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{Math.round(avgScore)}%</p>
                            <p className="text-xs text-muted-foreground">Avg Score</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{highestScore}%</p>
                            <p className="text-xs text-muted-foreground">Best Score</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold capitalize">{stats?.highest_difficulty_reached || 'N/A'}</p>
                            <p className="text-xs text-muted-foreground">Max Difficulty</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Available Labs Overview */}
                {labs.length > 0 && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Briefcase className="h-5 w-5 text-primary" />
                                Available Labs ({labs.length})
                            </CardTitle>
                            <CardDescription>Practice sourcing for different roles and industries</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                {labs.slice(0, 6).map((lab) => (
                                    <Card 
                                        key={lab.id} 
                                        className="cursor-pointer hover:border-primary/50 transition-colors"
                                        onClick={() => navigate(`/sourcing-labs?lab=${lab.id}`)}
                                    >
                                        <CardContent className="p-3">
                                            <p className="text-sm font-medium truncate">{lab.title || lab.role}</p>
                                            <p className="text-xs text-muted-foreground truncate mt-1">{lab.industry}</p>
                                            <div className="mt-2">
                                                {getDifficultyBadge(lab.difficulty)}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                            {labs.length > 6 && (
                                <Button 
                                    variant="outline" 
                                    className="w-full mt-3"
                                    onClick={() => navigate('/sourcing-labs')}
                                >
                                    View All {labs.length} Labs
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Gamification Progress */}
                {stats?.gamification && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Trophy className="h-5 w-5 text-amber-500" />
                                Achievements & Progress
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                                    <div className="flex items-center gap-2">
                                        <Star className="h-5 w-5 text-amber-500" />
                                        <span className="text-lg font-bold">{stats.gamification.points || 0}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">Total Points</p>
                                </div>
                                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                                    <div className="flex items-center gap-2">
                                        <Award className="h-5 w-5 text-purple-500" />
                                        <span className="text-lg font-bold">{stats.gamification.badges?.length || 0}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">Badges Earned</p>
                                </div>
                                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                                    <div className="flex items-center gap-2">
                                        <Zap className="h-5 w-5 text-blue-500" />
                                        <span className="text-lg font-bold">{stats.gamification.streak || 0}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">Day Streak</p>
                                </div>
                                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                                    <div className="flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5 text-emerald-500" />
                                        <span className="text-lg font-bold capitalize">{stats.gamification.level || 'Novice'}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">Current Level</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Filter:</span>
                    </div>
                    <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Difficulty" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Levels</SelectItem>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                            <SelectItem value="expert">Expert</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                        </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground ml-auto">
                        Showing {sessions.length} of {total} sessions
                    </span>
                </div>

                {/* Sessions Table with Infinite Scroll */}
                <Card>
                    <CardContent className="p-0">
                        <ScrollArea className="h-[500px]">
                            <Table>
                                <TableHeader className="sticky top-0 bg-background z-10">
                                    <TableRow>
                                        <TableHead className="w-[200px]">Lab / Role</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('difficulty')} className="h-8 px-2">
                                                Difficulty
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('status')} className="h-8 px-2">
                                                Status
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('scores.overall')} className="h-8 px-2">
                                                Score
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>Candidates</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('created_at')} className="h-8 px-2">
                                                Date
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sessionsLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-8">
                                                <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                                            </TableCell>
                                        </TableRow>
                                    ) : sessions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                No sourcing sessions yet. Start a lab to practice!
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        <>
                                            {sessions.map((session) => {
                                                const score = session.scores?.overall || 0;
                                                
                                                return (
                                                    <TableRow 
                                                        key={session.id} 
                                                        className="cursor-pointer hover:bg-muted/50"
                                                        onClick={() => viewSessionDetail(session)}
                                                    >
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <div className="p-1.5 rounded bg-primary/10">
                                                                    <Search className="h-3 w-3 text-primary" />
                                                                </div>
                                                                <span className="text-sm font-medium truncate max-w-[150px]">
                                                                    {session.lab_title || session.role || 'Sourcing Lab'}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>{getDifficultyBadge(session.difficulty)}</TableCell>
                                                        <TableCell>{getStatusBadge(session.status)}</TableCell>
                                                        <TableCell>
                                                            <span className={`font-bold ${getScoreColor(score)}`}>
                                                                {score > 0 ? `${Math.round(score)}%` : '-'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="text-sm">{session.candidates_found || 0}</span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="text-sm text-muted-foreground">
                                                                {session.created_at 
                                                                    ? formatDistanceToNow(new Date(session.created_at), { addSuffix: true })
                                                                    : '-'
                                                                }
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    viewSessionDetail(session);
                                                                }}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            
                                            {/* Infinite scroll sentinel */}
                                            <TableRow ref={loadMoreRef}>
                                                <TableCell colSpan={7} className="text-center py-4">
                                                    {loadingMore ? (
                                                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                            <span className="text-sm">Loading more...</span>
                                                        </div>
                                                    ) : hasMore ? (
                                                        <span className="text-sm text-muted-foreground">Scroll for more</span>
                                                    ) : sessions.length > 0 ? (
                                                        <span className="text-sm text-muted-foreground">All sessions loaded</span>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Detail Modal */}
                <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <div className="p-2 rounded bg-primary/10">
                                    <Search className="h-5 w-5 text-primary" />
                                </div>
                                {selectedSession?.lab_title || selectedSession?.role || 'Sourcing Lab Session'}
                            </DialogTitle>
                            <DialogDescription>
                                {selectedSession?.created_at && format(new Date(selectedSession.created_at), 'PPpp')}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {selectedSession && (
                            <div className="space-y-4">
                                {/* Scores */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className={`text-2xl font-bold ${getScoreColor(selectedSession.scores?.overall || 0)}`}>
                                                {Math.round(selectedSession.scores?.overall || 0)}%
                                            </p>
                                            <p className="text-xs text-muted-foreground">Overall</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSession.candidates_found || 0}</p>
                                            <p className="text-xs text-muted-foreground">Candidates Found</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSession.search_strings?.length || 0}</p>
                                            <p className="text-xs text-muted-foreground">Search Strings</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSession.time_spent || '-'}</p>
                                            <p className="text-xs text-muted-foreground">Time (min)</p>
                                        </CardContent>
                                    </Card>
                                </div>
                                
                                {/* Details */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Status</span>
                                        {getStatusBadge(selectedSession.status)}
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Difficulty</span>
                                        {getDifficultyBadge(selectedSession.difficulty)}
                                    </div>
                                    {selectedSession.industry && (
                                        <div className="flex justify-between p-2 bg-muted/50 rounded col-span-2">
                                            <span className="text-muted-foreground">Industry</span>
                                            <span>{selectedSession.industry}</span>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Feedback */}
                                {selectedSession.feedback && (
                                    <div className="p-3 rounded-lg bg-muted/50">
                                        <h4 className="font-medium mb-2 flex items-center gap-2">
                                            <Star className="h-4 w-4 text-amber-500" />
                                            Feedback
                                        </h4>
                                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                            {typeof selectedSession.feedback === 'string' 
                                                ? selectedSession.feedback 
                                                : selectedSession.feedback?.summary || 'No feedback available'
                                            }
                                        </p>
                                    </div>
                                )}
                                
                                {/* Actions */}
                                <div className="flex gap-2 pt-2">
                                    <Button 
                                        variant="outline" 
                                        className="flex-1"
                                        onClick={() => setShowDetailModal(false)}
                                    >
                                        Close
                                    </Button>
                                    <Button 
                                        className="flex-1"
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            navigate('/sourcing-labs');
                                        }}
                                    >
                                        <Play className="h-4 w-4 mr-2" />
                                        Start New Lab
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default SourcingLabsAnalytics;
